import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import { IMessageBubble } from "../../../models/interactions/MessageBubble";
import MessageBubbleAnimationDropdown from "./MessageBubbleAnimationDropdown";
import MessageBubbleActorDropdown from "./MessageBubbleActorDropdown";
import MessageBubblePreviewText from "./MessageBubblePreviewText";
import ActorPropDropdown from "../lookat/ActorPropDropdown";
import usePropHelper from "../../../hooks/PropHelper";

type MessageBubblePreviewBodyVariant = "standalone" | "embedded";

export default function MessageBubblePreviewBody(props: {
  messageBubble: IMessageBubble;
  updateInteraction: (field: string, value: any, patch?: Partial<IMessageBubble>) => void;
  variant?: MessageBubblePreviewBodyVariant;
}) {
  const { messageBubble, updateInteraction, variant = "embedded" } = props;
  const take = useSelector(selectSelectedTake3D);
  const { getBubbleTargetProps } = usePropHelper();
  const propsInTake = take
    ? getBubbleTargetProps(take, {
        kind: "timeline",
        interactionId: messageBubble.id,
      })
    : [];
  const isPropIdInTake = (objectId: string) =>
    propsInTake.some((prop) => prop.id === objectId);
  const isPropAnchor = isPropIdInTake(messageBubble.npcId);
  const handleAnchorChange = (objectId: string) => {
    if (isPropIdInTake(objectId)) {
      updateInteraction("npcId", objectId, { lookAt: "" });
      return;
    }

    updateInteraction("npcId", objectId);
  };

  useEffect(() => {
    if (!isPropAnchor || !messageBubble.lookAt) return;
    updateInteraction("lookAt", "");
  }, [isPropAnchor, messageBubble.lookAt, updateInteraction]);

  if (variant === "embedded") {
    return (
      <div className="w-full h-full flex flex-col gap-5">
        <div className="w-full grid grid-cols-1 piLg:grid-cols-[minmax(260px,34%)_minmax(320px,max-content)] gap-7 items-start">
          <div className="flex flex-col gap-5 min-w-0">
            <ActorPropDropdown
              label={"Actor"}
              selectedObjectId={messageBubble.npcId}
              onClick={handleAnchorChange}
              availableProps={propsInTake}
            />
            {isPropAnchor ? (
              <DisabledLookAtField label="Look at" />
            ) : (
              <MessageBubbleActorDropdown
                label={"Look at"}
                selectedTargetActorId={messageBubble.lookAt}
                onClick={(actorId) => updateInteraction("lookAt", actorId)}
                allowNoActor
              />
            )}
          </div>
          <div className="min-w-0">
            <SettingsSection label="Animation">
              <MessageBubbleAnimationDropdown
                selectedAnimation={messageBubble.animation}
                onClick={(anim) => updateInteraction("animation", anim)}
                variant="preview"
              />
            </SettingsSection>
          </div>
        </div>
        <div className="w-full flex-1 min-h-0">
          <MessageBubblePreviewText
            messageBubble={messageBubble}
            updateInteraction={updateInteraction}
            variant="embedded"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full flex flex-col gap-4 rounded-xl overflow-hidden border"
      style={{
        borderColor: "var(--pi-game-blue-faded)",
        backgroundColor: "var(--pi-surface)",
      }}
    >
      <div
        className="grid grid-cols-1 piLg:grid-cols-2 gap-4 px-5 pt-5"
      >
        <SettingsSection label="Actor">
          <ActorPropDropdown
            label={""}
            selectedObjectId={messageBubble.npcId}
            onClick={handleAnchorChange}
            availableProps={propsInTake}
          />
        </SettingsSection>
        <SettingsSection label="Looks at">
          {isPropAnchor ? (
            <DisabledLookAtField label="" />
          ) : (
            <MessageBubbleActorDropdown
              label={""}
              selectedTargetActorId={messageBubble.lookAt}
              onClick={(actorId) => updateInteraction("lookAt", actorId)}
              allowNoActor
            />
          )}
        </SettingsSection>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 piLg:grid-cols-[minmax(0,1fr)_minmax(320px,440px)] px-5 pb-5 gap-4">
        <SettingsSection label="Message">
          <section
            className="relative min-h-0 flex-1 rounded-lg border flex flex-col overflow-hidden"
            style={{
              borderColor: "var(--pi-game-blue-faded)",
              backgroundColor: "var(--pi-game-blue-faded)",
            }}
          >
            <div className="flex-1 min-h-0 flex items-center justify-center px-4 py-4">
              <div className="w-full">
                <MessageBubblePreviewText
                  messageBubble={messageBubble}
                  updateInteraction={updateInteraction}
                  variant="standalone"
                />
              </div>
            </div>
          </section>
        </SettingsSection>

        <SettingsSection label="Animation">
          <section
            className="min-h-0 flex-1 rounded-lg border flex flex-col overflow-hidden"
            style={{
              borderColor: "var(--pi-game-blue-faded)",
              backgroundColor: "var(--pi-game-blue-faded)",
            }}
          >
            <div className="flex-1 min-h-0 flex flex-col px-4 py-4">
              <MessageBubbleAnimationDropdown
                selectedAnimation={messageBubble.animation}
                onClick={(anim) => updateInteraction("animation", anim)}
                variant="standalone"
              />
            </div>
          </section>
        </SettingsSection>
      </div>
    </div>
  );
}

function DisabledLookAtField(props: { label: string }) {
  return (
    <div className="w-full h-[98px] opacity-60">
      {props.label && (
        <div className="text-base font-bold text-interaction-primary h-[30px]">
          {props.label}
        </div>
      )}
      <div className="flex p-4 w-full h-[68px] align-middle border-b-2 border-solid bg-interaction-softBackground border-interaction-primary items-center cursor-not-allowed">
        <div className="w-full font-medium text-interaction-primary text-interaction text-interaction-base">
          No actor
        </div>
      </div>
    </div>
  );
}

function SettingsSection(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 min-w-0 min-h-0">
      {props.label && (
        <div className="text-base font-bold text-interaction-primary">
          {props.label}
        </div>
      )}
      {props.children}
    </div>
  );
}
